import { Dialog, DialogActions, DialogContent, DialogTitle, Grid } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { useEffect, useState } from 'react';

import { SnackbarOpen } from '#app/Action/SnackbarAction';
import { dispatch } from '#app/App';
import ButtonComponent from '#app/Component/ButtonComponent';
import CheckboxComponent from '#app/Component/CheckboxComponent';
import MultipleSelectComponent from '#app/Component/MultipleSelectComponent';
import SelectComponent from '#app/Component/SelectComponent';
import TextFieldComponent from '#app/Component/TextFieldComponent';
import ApiHandler from '#app/Handler/ApiHandler';
import { Form, initFormHandler } from '#app/Handler/FormHandler';
import useUserPreparers from '#app/View/InventoryManagement/PreparerManager/hooks/useUserPreparers';
import { CarrierCode } from '#app/typings/carrier';
import { UserPreparer } from '#app/typings/userPreparer';
import { Option } from '#app/typings/utils';

type Props = {
  onClose: () => void;
  preparer: UserPreparer;
};

const EditPreparerModal: React.FC<Props> = ({ onClose, preparer }) => {
  const classes = useStyles();
  const { mutate } = useUserPreparers();
  const [loading, setLoading] = useState<boolean>(false);
  const [optionsCarrier, setOptionsCarrier] = useState<Option[]>([]);
  const [optionsPrinter, setOptionsPrinter] = useState<Option[]>([]);
  const [optionsLabelPrinter, setOptionsLabelPrinter] = useState<Option[]>([]);
  const [optionsChannel, setOptionsChannel] = useState<Option[]>([]);

  const [form, setForm] = useState<Form>({
    isActive: {
      name: 'isActive',
      label: 'Actif',
      type: 'boolean',
      defaultValue: preparer.isActive,
    },
    amount: {
      name: 'amount',
      label: 'Quantité commande simultanée',
      textHelper: 'Saisissez la quantité de commande simultanée.',
      type: 'integer',
      defaultValue: preparer.amount,
      options: { validation: ['required'] },
    },
    channel: {
      name: 'channel',
      label: 'Canal',
      textHelper: 'Choisissez un canal.',
      type: 'integer',
      defaultValue: preparer.channel,
      options: { validation: ['required'] },
    },
    carriers: {
      name: 'carriers',
      label: 'Transporteurs',
      textHelper: 'Choisissez un transporteur.',
      type: 'array',
    },
    printer: {
      name: 'printer',
      label: 'Imprimante',
      textHelper: 'Choisissez une imprimante.',
      type: 'integer',
      defaultValue: preparer.printer,
      options: { validation: ['required'] },
    },
    labelPrinter: {
      name: 'labelPrinter',
      label: 'Imprimante à étiquette',
      textHelper: 'Choisissez une imprimante à étiquette.',
      type: 'integer',
      defaultValue: preparer.labelPrinter,
      options: { validation: ['required'] },
    },
  });

  const handler = initFormHandler(form, setForm);

  useEffect(() => {
    ApiHandler.get<Option[]>({ route: 'api_select_carrier' }, (response) => {
      setOptionsCarrier(response.data ?? []);
      handler.setValue(
        'carriers',
        (response.data ?? [])
          .filter((carrier) => preparer.carriers.includes(carrier.code as CarrierCode))
          .map((carrier) => carrier.value),
      );
    });
    ApiHandler.get<Option[]>({ route: 'api_select_printer' }, (response) => setOptionsPrinter(response.data ?? []));
    ApiHandler.get<Option[]>({ route: 'api_select_label_printer' }, (response) =>
      setOptionsLabelPrinter(response.data ?? []),
    );
    ApiHandler.get<Option[]>({ route: 'api_select_channel' }, (response) => setOptionsChannel(response.data ?? []));

    handler.start();
  }, []);

  const handleSubmit = () => {
    if (handler.checkError() > 0) {
      return;
    }
    handler.setFormLoading(true);
    setLoading(true);

    ApiHandler.post(
      {
        route: 'api_inventory_management_preparer_manager_preparer_edit',
        params: { id: preparer.id },
        data: handler.getData(),
      },
      (response) => {
        if (response.status === 200) {
          dispatch(
            SnackbarOpen({
              text: 'Modification enregistrée avec succès.',
              variant: 'success',
            }),
          );
          mutate();
          onClose();
        } else {
          dispatch(
            SnackbarOpen({
              text: response?.error?.message || "Une erreur inattendue s'est produite.",
              variant: 'error',
            }),
          );
        }
        handler.setFormLoading(false);
        setLoading(false);
      },
    );
  };

  return (
    <Dialog open maxWidth="xl" onClose={onClose}>
      <DialogTitle>{preparer.name}</DialogTitle>
      <DialogContent className={classes.content}>
        <Grid container spacing={1}>
          <Grid item xs={4} sm={2}>
            <CheckboxComponent id="isActive" handler={handler} orientation={'right'} />
          </Grid>
          <Grid item xs={8} sm={2}>
            <TextFieldComponent id="amount" handler={handler} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <SelectComponent id="printer" options={optionsPrinter} handler={handler} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <SelectComponent id="labelPrinter" options={optionsLabelPrinter} handler={handler} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <SelectComponent id="channel" options={optionsChannel} handler={handler} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <MultipleSelectComponent id="carriers" options={optionsCarrier} handler={handler} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <ButtonComponent color="#5E6E82" label="Annuler" onClick={onClose} disabled={loading} />
        <ButtonComponent label="Enregistrer" onClick={handleSubmit} loading={loading} />
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles({
  content: {
    minWidth: 400,
  },
});

export default EditPreparerModal;
